import { computed } from 'vue';
import type { ComputedRef } from 'vue';

type UsePlatformReturnType = {
    isMac: ComputedRef<boolean>;
    formatShortcut: (key: string) => string;
    getModifierKey: () => string;
};

/**
 * Composable for detecting the operating system and formatting keyboard shortcuts
 *
 * @returns {UsePlatformReturnType} Platform detection utilities
 *
 * @example
 * const { isMac, formatShortcut, getModifierKey } = usePlatform()
 *
 * // On macOS: formatShortcut('K') => "⌘K"
 * // On Windows/Linux: formatShortcut('K') => "Ctrl+K"
 *
 * // Get modifier key
 * const modifier = getModifierKey() // "⌘" on macOS, "Ctrl" on others
 */
export function usePlatform(): UsePlatformReturnType {
    /**
     * Detects if the current platform is macOS
     */
    const isMac = computed<boolean>(() => {
        const userAgent = navigator.userAgent.toLowerCase();

        return userAgent.includes('macintosh') || userAgent.includes('mac os');
    });

    /**
     * Gets the modifier key based on the platform
     *
     * @returns {string} "⌘" for macOS, "Ctrl" for other platforms
     */
    const getModifierKey = (): string => {
        return isMac.value ? 'Cmd' : 'Ctrl';
    };

    /**
     * Formats a keyboard shortcut based on the platform
     *
     * @param {string} key - The key to format (e.g., 'K', 'S', 'Enter')
     * @returns {string} Formatted shortcut (e.g., "⌘K" on macOS, "Ctrl+K" on others)
     *
     * @example
     * formatShortcut('K') // "⌘K" on macOS, "Ctrl+K" on Windows/Linux
     * formatShortcut('S') // "⌘S" on macOS, "Ctrl+S" on Windows/Linux
     */
    const formatShortcut = (key: string): string => {
        const modifier = getModifierKey();

        return `${modifier}+${key}`;
    };

    return {
        isMac,
        formatShortcut,
        getModifierKey,
    };
}
